import { Slider as ArkSlider, type SliderRootProps as ArkSliderRootProps } from '@ark-ui/react';

import type { ActionColor, Radius, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type SliderProps = Omit<ArkSliderRootProps, 'color'> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft';
	color?: ActionColor;
	highContrast?: boolean;
	radius?: Radius;
};

function SliderRoot({
	className,
	size = '2',
	variant = 'surface',
	color = 'accent',
	highContrast,
	radius,
	children,
	...props
}: SliderProps) {
	return (
		<ArkSlider.Root
			className={cn('pt-slider', className)}
			data-size={size}
			data-variant={variant}
			data-accent-color={color}
			data-high-contrast={highContrast ? '' : undefined}
			data-radius={radius}
			{...props}
		>
			{children}
		</ArkSlider.Root>
	);
}

function SliderTrack(props: ArkSlider.TrackProps) {
	return <ArkSlider.Track className={cn('pt-slider-track', props.className)} {...props} />;
}

function SliderRange(props: ArkSlider.RangeProps) {
	return <ArkSlider.Range className={cn('pt-slider-range', props.className)} {...props} />;
}

function SliderThumb(props: ArkSlider.ThumbProps) {
	return <ArkSlider.Thumb className={cn('pt-slider-thumb', props.className)} {...props} />;
}

function SliderControl(props: ArkSlider.ControlProps) {
	return <ArkSlider.Control className={cn('pt-slider-control', props.className)} {...props} />;
}

function SliderLabel(props: ArkSlider.LabelProps) {
	return <ArkSlider.Label className={cn('pt-slider-label', props.className)} {...props} />;
}

function SliderValueText(props: ArkSlider.ValueTextProps) {
	return <ArkSlider.ValueText className={cn('pt-slider-value-text', props.className)} {...props} />;
}

const Slider = Object.assign(SliderRoot, {
	Root: SliderRoot,
	Control: SliderControl,
	Track: SliderTrack,
	Range: SliderRange,
	Thumb: SliderThumb,
	Label: SliderLabel,
	ValueText: SliderValueText,
	MarkerGroup: ArkSlider.MarkerGroup,
	Marker: ArkSlider.Marker,
	HiddenInput: ArkSlider.HiddenInput
});

export { Slider, type SliderProps };
